import type {
  ProfilePaymentMethodsViewModelFactory,
  StripeConnectCallbackModalViewModelFactory,
} from '@/features/billing'
import type {
  IsPayoutsEnabledFn,
  ProfilePayoutMethodViewModelFactory,
} from '@/features/payouts'
import type { NavigateFn } from '@/RootStore'
import { type Member } from '@/features/iam/members/Member'
import { computed } from 'mobx'
import { links } from '@/utils/link'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * View model for the Billing tab.
 */
export class ProfileBillingViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly stripeConnectModalVm
  readonly paymentMethodsVm
  readonly payoutMethodVm

  constructor(
    private readonly _member: Member,
    private readonly _isPayoutsEnabled: IsPayoutsEnabledFn,
    private readonly _createProfilePaymentMethodsViewModel: ProfilePaymentMethodsViewModelFactory,
    private readonly _createProfilePayoutMethodViewModel: ProfilePayoutMethodViewModelFactory,
    private readonly _createStripeConnectCallbackModalViewModel: StripeConnectCallbackModalViewModelFactory,
    private readonly _navigate: NavigateFn,
  ) {
    this.stripeConnectModalVm = this._createStripeConnectCallbackModalViewModel(
      (code) => this.payoutMethodVm.connectStripe(code),
    )

    this.paymentMethodsVm = this._createProfilePaymentMethodsViewModel(
      this._member,
    )

    this.payoutMethodVm = this._createProfilePayoutMethodViewModel(this._member)

    this._lifecycle.manage(this.paymentMethodsVm)
    if (this.isPayoutsEnabled) {
      this._lifecycle.manage(this.payoutMethodVm)
    }
  }

  /**
   * Determines if payouts is enabled.
   */
  @computed
  get isPayoutsEnabled() {
    return this._isPayoutsEnabled()
  }

  activate() {
    this._lifecycle.activate()
    // The Stripe Connect modal shows failures.
    this._load().catch(() => undefined)
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  private async _load() {
    // Complete stripe payout connection
    if (this.stripeConnectModalVm.hasPendingConnection()) {
      this._navigate(links.profileBillingPayoutMethod())
      await this.stripeConnectModalVm.connect()
    }
  }
}
